import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import type {
  DirectedCastDescription,
  DirectedCastEntry,
  DirectedCastRole,
} from 'fym-shared/src/music24/directedVideoApi';
import { DIRECTED_CAST_LABEL_MAX_LENGTH } from 'fym-shared/src/music24/directedVideoApi';
import { TypedMemo } from '../../common/types';
import { castMessages as messages } from '../messages/castMessages';
import { useDirectedCastNames } from '../useDirectedCastNames';
import { CastDescription } from './CastDescription';
import { CastRoles } from './CastRoles';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the directed video; each part uses its own slice of it */
import styles from '../DirectedVideo.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CastCardProps {
  entry: DirectedCastEntry;
  /** The role on screen: the stored role, or Person while it waits for the consent tick. */
  role: DirectedCastRole;
  readOnly: boolean;
  onLabel: (label: string) => void;
  onRole: (role: DirectedCastRole) => void;
  onDescription: (description: DirectedCastDescription | null) => void;
  onRemove: () => void;
}

/**
 * One picture of the cast (design b1 `.cc`): the photo with the remove button and the "Made by AI"
 * tag, the label field (saved when it loses the focus or on Enter), the four roles and what the AI
 * read in the picture (the artist corrects it).
 */
const CastCardComponent: React.FC<CastCardProps> = ({
  entry,
  role,
  readOnly,
  onLabel,
  onRole,
  onDescription,
  onRemove,
}) => {
  const intl = useIntl();
  const [label, setLabel] = useState(entry.label);
  useEffect(() => setLabel(entry.label), [entry.label]);
  // The display name: the label, the artist name for "me", else "Character N" (owner report 2026-10-18).
  const { names } = useDirectedCastNames();
  const name = names.get(entry.id) ?? intl.formatMessage(messages.unnamed);
  const defaultName = names.get(entry.id) ?? intl.formatMessage(messages.labelPlaceholder);

  const save = () => {
    const next = label.trim();
    if (next !== entry.label.trim()) {
      onLabel(next);
    }
  };

  return (
    <div className={styles.castCard} data-testid="cast-card">
      <div className={styles.picture}>
        {entry.url === '' ? (
          <div className={`${styles.picture} ${styles.picturePlaceholder}`}>
            <span>{intl.formatMessage(messages.noPicture)}</span>
          </div>
        ) : (
          <img src={entry.url} alt={name} />
        )}
        {entry.generated && <span className={styles.aiTag}>{intl.formatMessage(messages.madeByAi)}</span>}
        {!readOnly && (
          <button
            type="button"
            className={styles.remove}
            aria-label={intl.formatMessage(messages.remove, { label: name })}
            onClick={onRemove}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        )}
      </div>
      <div className={styles.castBody}>
        {readOnly ? (
          <div className={styles.labelText}>{name}</div>
        ) : (
          <input
            className={styles.labelInput}
            value={label}
            maxLength={DIRECTED_CAST_LABEL_MAX_LENGTH}
            placeholder={entry.label.trim() === '' ? defaultName : intl.formatMessage(messages.labelPlaceholder)}
            aria-label={intl.formatMessage(messages.labelAria)}
            onChange={(event) => setLabel(event.target.value)}
            onBlur={save}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.currentTarget.blur();
              }
            }}
          />
        )}
        <CastRoles value={role} label={name} disabled={readOnly} onChange={onRole} />
        <CastDescription entry={entry} readOnly={readOnly} onSave={onDescription} />
      </div>
    </div>
  );
};

export const CastCard = TypedMemo(CastCardComponent);
